const WATER_ORIGIN_WHEEL_CAPTURE = { capture: true, passive: false };

if (!customElements.get('water-origin-swiper')) {
  customElements.define(
    'water-origin-swiper',
    class WaterOriginSwiper extends HTMLElement {
      constructor() {
        super();
        this._onWheelCapture = this._onWheelCapture.bind(this);
      }

      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;
        this._mq = window.matchMedia('(max-width: 767.98px)');
        this._onMqChange = this._onMqChange.bind(this);
        if (this._mq.addEventListener) {
          this._mq.addEventListener('change', this._onMqChange);
        } else {
          this._mq.addListener(this._onMqChange);
        }
        requestAnimationFrame(() => this.initSlider());
      }

      disconnectedCallback() {
        this.removeEventListener('wheel', this._onWheelCapture, WATER_ORIGIN_WHEEL_CAPTURE);
        this._initialized = false;
        if (this._mq && this._onMqChange) {
          if (this._mq.removeEventListener) {
            this._mq.removeEventListener('change', this._onMqChange);
          } else {
            this._mq.removeListener(this._onMqChange);
          }
        }
        this._mq = null;
        this._onMqChange = null;
        if (this._slider?.destroy) {
          this._slider.destroy(true, true);
        }
        this._slider = null;
      }

      _onMqChange() {
        if (!this._initialized || !this.isConnected) return;
        requestAnimationFrame(() => this.initSlider());
      }

      _onWheelCapture(e) {
        const s = this._slider;
        if (!s || s.destroyed) return;
        if (!this.contains(e.target)) return;
        if (e.deltaY === 0) return;
        if (e.deltaY > 0 && !s.isEnd) e.preventDefault();
        if (e.deltaY < 0 && !s.isBeginning) e.preventDefault();
      }

      initSlider() {
        const swiperContainer = this.querySelector('.water-origin-container');
        if (!swiperContainer) return;

        const prevIndex = this._slider && !this._slider.destroyed ? this._slider.activeIndex : 0;

        if (this._slider?.destroy) {
          this._slider.destroy(true, true);
          this._slider = null;
        }

        const isMobile = this._mq
          ? this._mq.matches
          : window.matchMedia('(max-width: 767.98px)').matches;

        const parsedSpeed = parseInt(this.dataset.transitionMs, 10);
        const speed =
          Number.isFinite(parsedSpeed) && parsedSpeed >= 0 ? parsedSpeed : 300;

        const options = {
          speed,
          grabCursor: true,
          initialSlide: 0,
          scrollbarHide: true,
          navigation: {
            nextEl: this.querySelector(".swiper-button-next"),
            prevEl: this.querySelector(".swiper-button-prev"),
          },
          direction: "vertical",
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
          },
          pagination: {
            el: this.querySelector(".swiper-pagination"),
            clickable: true,
          },
          on: {
            init: () => {
              this.swiperAnimateCache();
              this.swiperAnimate(swiperContainer, swiperContainer.querySelector('.swiper-slide-active'));
              this.renderPaginationStyle();
            },
            slideChangeTransitionEnd: (swiperInstance) => {
              this.swiperAnimate(swiperContainer, swiperInstance.slides[swiperInstance.activeIndex]);
            },
          },
        };

        if (isMobile) {
          options.effect = 'slide';
        } else {
          options.effect = 'fade';
          options.fadeEffect = {
            crossFade: true,
          };
        }

        if (window.FoxTheme && window.FoxTheme.Carousel) {
          const S = window.FoxTheme.Swiper;
          const additionModules = [];
          if (!isMobile && S && S.EffectFade) additionModules.push(S.EffectFade);
          if (S && S.Mousewheel) additionModules.push(S.Mousewheel);
          const carousel =
            additionModules.length > 0
              ? new window.FoxTheme.Carousel(swiperContainer, options, additionModules)
              : new window.FoxTheme.Carousel(swiperContainer, options);
          carousel.init();
          this._slider = carousel.slider;
        } else if (typeof Swiper !== 'undefined') {
          this._slider = new Swiper(swiperContainer, options);
        } else {
          console.error("Swiper or FoxTheme.Carousel is not available");
        }

        if (this._slider && prevIndex > 0 && !this._slider.destroyed) {
          this._slider.slideTo(prevIndex, 0);
        }

        if (this._slider) {
          this.removeEventListener('wheel', this._onWheelCapture, WATER_ORIGIN_WHEEL_CAPTURE);
          this.addEventListener('wheel', this._onWheelCapture, WATER_ORIGIN_WHEEL_CAPTURE);
        }
      }

      renderPaginationStyle() {
        const slides = this.querySelectorAll('.swiper-slide');
        const bullets = this.querySelectorAll(".swiper-pagination-bullet");
        bullets.forEach((bullet, index) => {
          if (slides[index]) {
            const titleEl = slides[index].querySelector('.ani.title');
            if (titleEl) {
              bullet.setAttribute("data-content", titleEl.textContent.trim());
            }
          }
        });
      }

      swiperAnimateCache() {
        const allBoxes = this.querySelectorAll(".ani");
        for (let i = 0; i < allBoxes.length; i++) {
          if (allBoxes[i].attributes["style"]) {
            allBoxes[i].setAttribute("swiper-animate-style-cache", allBoxes[i].attributes["style"].value);
          } else {
            allBoxes[i].setAttribute("swiper-animate-style-cache", " ");
          }
          allBoxes[i].style.visibility = "hidden";
        }
      }

      swiperAnimate(container, activeSlide) {
        this.clearSwiperAnimate(container);
        if (!activeSlide) return;

        const b = activeSlide.querySelectorAll(".ani");
        for (let i = 0; i < b.length; i++) {
          b[i].style.visibility = "visible";
          const effect = b[i].attributes["swiper-animate-effect"]
            ? b[i].attributes["swiper-animate-effect"].value
            : "";
          b[i].className = b[i].className + " " + effect + " " + "animated";
          const duration = b[i].attributes["swiper-animate-duration"]
            ? b[i].attributes["swiper-animate-duration"].value
            : "";
          const delay = b[i].attributes["swiper-animate-delay"]
            ? b[i].attributes["swiper-animate-delay"].value
            : "";

          const styleCache = b[i].attributes["style"] ? b[i].attributes["style"].value : "";
          const style =
            styleCache +
            "animation-duration:" + duration +
            ";-webkit-animation-duration:" + duration +
            ";animation-delay:" + delay +
            ";-webkit-animation-delay:" + delay + ";";
          b[i].setAttribute("style", style);
        }
      }

      clearSwiperAnimate(container) {
        const allBoxes = container.querySelectorAll(".ani");
        for (let i = 0; i < allBoxes.length; i++) {
          if (allBoxes[i].attributes["swiper-animate-style-cache"]) {
            allBoxes[i].setAttribute(
              "style",
              allBoxes[i].attributes["swiper-animate-style-cache"].value
            );
          }
          allBoxes[i].style.visibility = "hidden";
          allBoxes[i].className = allBoxes[i].className.replace("animated", " ").trim();

          if (allBoxes[i].attributes["swiper-animate-effect"]) {
            const effectValue = allBoxes[i].attributes["swiper-animate-effect"].value;
            allBoxes[i].className = allBoxes[i].className.replace(effectValue, " ").trim();
          }
        }
      }
    }
  );
}
